const toLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Returns true when a task's available date (calendar date) is today or earlier.
 *
 * The API serialises AvailableDate as a UTC datetime ("YYYY-MM-DDT00:00:00Z").
 * Parsing that with `new Date(...)` shifts the calendar day backwards in
 * UTC-negative timezones, so a task due to unlock tomorrow looked available today.
 * Comparing the date portion against the local calendar date avoids any timezone shift.
 */
export const isTaskAvailable = (availableDate: string, now: Date = new Date()): boolean => {
  const availableDay = availableDate.slice(0, 10);
  return availableDay <= toLocalDateString(now);
};

/**
 * Orders tasks for display: every task the patient can complete right now
 * comes first, then the not-yet-available ones — each group sorted by due
 * date so a doable task is never buried under a locked future one.
 */
export const sortTasksByAvailability = <
  T extends { availableDate: string; dueDate: string },
>(
  tasks: readonly T[],
  now: Date = new Date(),
): T[] =>
  [...tasks].sort((a, b) => {
    const aAvailable = isTaskAvailable(a.availableDate, now);
    const bAvailable = isTaskAvailable(b.availableDate, now);
    if (aAvailable !== bAvailable) return aAvailable ? -1 : 1;
    return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
  });
